import { useContext, useState, useEffect } from 'react';
import InputField from 'components/common/input-field';
import PrimaryButtom from 'components/common/buttons/primary-btn';
import { emailRegEx, getRedirectedURL } from 'helpers/utils';
import { useNavigate } from 'react-router-dom';
import { login } from 'store/features/authSlice';
import { useAppDispatch } from 'hooks/useAppDispatchSelector';
import { setItemInLocalStorage } from 'helpers/localstorage';
import { AppContext } from 'context/app-context';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ILoginDetails } from 'interfaces/auth-pages-interfaces';

const initData = {
  email: '',
  password: '',
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { setLoader } = useContext(AppContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loginData, setLoginData] = useState<ILoginDetails>(initData);
  const [error, setError] = useState<ILoginDetails>(initData);
  const handleChange = (field: string, value: string) => {
    setLoginData({ ...loginData, [field]: value });
  };
  const setErrors = () => {
    const { email, password } = loginData || {};
    setError({
      ...error,
      email: !email.trim()
        ? t('PLEASE_ENTER_EMAIL')
        : !emailRegEx.test(email?.trim())
        ? t('PLEASE_ENTER_VALID_EMAIL')
        : '',
      password: !password.trim() ? t('PLEASE_ENTER_PASSWORD') : '',
    });
  };
  const isValid = () => {
    const { email, password } = loginData || {};
    setErrors();
    return !emailRegEx.test(email?.trim()) || !email.trim() || !password.trim() ? false : true;
  };
  const handleLogin = async () => {
    if (!isValid()) {
      return;
    }
    setLoader(true);
    dispatch(login({ request: loginData }))
      .then(async response => {
        if (response && response?.login_details.length) {
          setItemInLocalStorage('user', response);
          setItemInLocalStorage('org', response?.login_details[0]);
          const url = getRedirectedURL();
          navigate(url);
          setLoader(false);
        } else {
          toast.error(t('SOMETHING_WENT_WRONG'));
        }
      })
      .catch(() => {
        setLoader(false);
      });
  };
  useEffect(() => {
    const keyEnter = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (!isValid()) {
          return;
        }
        event.preventDefault();
        handleLogin();
      }
    };
    document.addEventListener('keydown', keyEnter);
    return () => {
      document.removeEventListener('keydown', keyEnter);
    };
  }, [loginData?.email, loginData?.password]);

  return (
    <div className="flex flex-col items-center w-full gap-y-4">
      <p className="font-semibold text-3xl text-center w-full">{t('SIGN_IN')}</p>
      <p className="text-lg text-center text-grey2 w-full">{t('SIGN_IN_SUBTITLE')}</p>
      <div className="flex flex-col w-full gap-y-2">
        <p className="text-sm w-full">{t('EMAIL')}</p>
        <InputField
          value={loginData?.email || ''}
          onChange={(data: string) => handleChange('email', data)}
          placeholder={t('ENTER_USERNAME')}
          error={error?.email || ''}
          disabled={false}
        />
      </div>
      <div className="flex flex-col w-full gap-y-2">
        <p className="text-sm w-full">{t('PASSWORD')}</p>
        <InputField
          value={loginData?.password || ''}
          onChange={(data: string) => handleChange('password', data)}
          placeholder={t('ENTER_PASSWORD')}
          error={error?.password || ''}
          disabled={false}
          type="password"
        />
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-x-2">
          {/* <CheckBox checked={rememberMe} onChange={checked => setRememberMe(checked)} /> */}
          {/* <p className="text-base font-regular text-grey2">Remember me?</p> */}
        </div>
        <div className="flex items-center" onClick={() => navigate('/forgot-password')}>
          <p className="text-base text-end font-semibold text-primary cursor-pointer">
            {t('FORGOT_PASSWORD')}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center mt-8 w-52">
        <PrimaryButtom
          label={t('SIGN_IN')}
          onClick={() => handleLogin()}
          width="w-52"
          disabled={error.password || error.email ? true : false}
        />
      </div>
    </div>
  );
};

export default Login;
